// Firestore Terms of Service & Version Management Service
import { db } from './firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import type { DadProfile, TermsConfig } from '../types';
import { getCurrentProfile, updateDadProfile } from './authService';

export const DEFAULT_TERMS_CONFIG: TermsConfig = {
  version: '1.0',
  title: 'Platform Scope & Legal Operating Terms',
  lastUpdated: 'September 2026',
  summary: 'Pops Culture is strictly an open communication noticeboard facilitating voluntary peer gatherings. We do not inspect work sites, oversee safety, or certify trade skills.',
  sections: [
    {
      title: '1. Facilitator Status & Independent Adult Assembly',
      content: 'Pops Culture is strictly a communications and mutual discovery software application designed to help adult fathers discover common volunteer and neighborly DIY interests. The platform facilitates introductions and discussion only. We are not a general contractor, construction manager, architectural service, or credentialing authority.'
    },
    {
      title: '2. Complete Absence of Credentialing',
      content: 'Pops Culture does not vet, license, verify, background-check, or authenticate any user\'s skills, trade certifications, or safety history. Any skill badges represent self-declared amateur hobbies and peer interests, not warrantied vocational capability.'
    },
    {
      title: '3. Permits, HOA, Zoning & Code Compliance',
      content: 'Event hosts bear 100% sole legal responsibility for verifying and obtaining all requisite municipal building permits, structural inspections, HOA approvals, and utility markouts (e.g., 811 Call Before You Dig). Pops Culture accepts zero responsibility for unpermitted structures or municipal code fines.'
    },
    {
      title: '4. Assumption of Risk & Personal Protective Equipment (PPE)',
      content: 'Working with construction tools, heavy materials, lumber, and power equipment carries inherent physical risks. Every attendee is an independent volunteer who assumes all personal risk of property damage or physical injury. Users are strictly responsible for assessing their own capabilities and bringing proper personal protective equipment (safety glasses, hearing protection, work boots).'
    },
    {
      title: '5. Child Privacy & Safety Protocol',
      content: 'Family squad profiles display only broad age brackets and general recreational interests (e.g., "Son (8) - Little League") to help fathers identify common playdate opportunities. No full names, schools, birthdates, or identifying photos of minors are required or displayed publicly.'
    },
    {
      title: '6. Media & Photography Release Terms',
      content: 'Pops Culture, event hosts, and participating members may capture photographs and video recordings during community gatherings, workshops, and builds. By attending, you agree, on behalf of yourself and all family members, minors, or guests attending with you, to grant Pops Culture the right to capture and use photos, video, and likeness for website, community noticeboards, and marketing purposes as described. You warrant that you have the legal authority to grant this permission for all attendees in your party.'
    }
  ],
  updatedAt: Date.now()
};

let currentTerms: TermsConfig = { ...DEFAULT_TERMS_CONFIG };
type TermsListener = (terms: TermsConfig) => void;
const listeners: TermsListener[] = [];

export function getCachedTermsConfig(): TermsConfig {
  return currentTerms;
}

export function subscribeTerms(listener: TermsListener): () => void {
  listeners.push(listener);
  listener(currentTerms);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

function notifyTermsListeners(terms: TermsConfig) {
  currentTerms = terms;
  listeners.forEach(fn => fn(terms));
}

let hasAttemptedTermsSync = false;

// Initialize Firestore Real-time Listener for system/terms
export function initTermsListener(): () => void {
  if (!db || !db.app) {
    return () => {};
  }

  const termsDocRef = doc(db, 'system', 'terms');

  const unsubscribe = onSnapshot(termsDocRef, async (snap) => {
    if (snap.exists()) {
      const data = snap.data() as TermsConfig;
      notifyTermsListeners({
        ...DEFAULT_TERMS_CONFIG,
        ...data
      });
    } else {
      if (hasAttemptedTermsSync) {
        notifyTermsListeners(DEFAULT_TERMS_CONFIG);
        return;
      }
      hasAttemptedTermsSync = true;

      // First-time seed of system/terms in Firestore
      try {
        await setDoc(termsDocRef, DEFAULT_TERMS_CONFIG);
        notifyTermsListeners(DEFAULT_TERMS_CONFIG);
      } catch (err) {
        console.warn('[Pops Culture] Could not seed terms in Firestore, using default:', err);
        notifyTermsListeners(DEFAULT_TERMS_CONFIG);
      }
    }
  }, (error) => {
    console.warn('[Pops Culture] Terms listener encountered an issue, using cached:', error);
    notifyTermsListeners(DEFAULT_TERMS_CONFIG);
  });

  return unsubscribe;
}

export function hasAcceptedCurrentTerms(profile: DadProfile | null, currentVersion: string = currentTerms.version): boolean {
  if (!profile) return false;
  return Boolean(
    profile.acceptedTermsVersion && 
    profile.acceptedTermsVersion === currentVersion && 
    profile.acceptedMediaRelease
  );
}

export async function acceptTerms(version: string = currentTerms.version): Promise<DadProfile> {
  const profile = getCurrentProfile();
  if (!profile) {
    throw new Error('User must be signed in to accept Terms of Service.');
  }

  const now = Date.now();
  const updated = await updateDadProfile({
    acceptedTermsVersion: version,
    acceptedTermsAt: now,
    acceptedMediaRelease: true,
    acceptedMediaReleaseAt: now
  });

  return updated;
}

// Programmatic version bump helper for testing or admin operations
export async function setTermsVersionInDB(newVersion: string, updatedTitle?: string): Promise<void> {
  const termsDocRef = doc(db, 'system', 'terms');
  const snap = await getDoc(termsDocRef);
  const existing = snap.exists() ? (snap.data() as TermsConfig) : DEFAULT_TERMS_CONFIG;

  const updated: TermsConfig = {
    ...existing,
    version: newVersion,
    lastUpdated: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    title: updatedTitle || existing.title,
    updatedAt: Date.now()
  };

  await setDoc(termsDocRef, updated, { merge: true });
}
